import {Button, TextField} from "@mui/material";
import {EmailLocation} from "../../locations/EmailLocation";
import {extractEmailAddresses} from "../../util/Formatters";
import React, {FC, useRef, useState} from "react";
import DbDraftEmail from "../../domain/DbDraftEmail";
import {AttachmentManagerView} from "./AttachmentManagerView";
import {FileUploadControlCallback} from "../common/FileUploadControl";
import {useAppDispatch, useAppSelector, useAppStore} from "../../hooks/ReduxHooks";
import {ComposeEmailActions} from "../../actions/ComposeEmailActions";
import {SenderSelectControl} from "./SenderSelectControl";
import {validateEmailToSend} from "../../validations/EmailValidator";
import ErrorDialog from "../common/ErrorDialog";
import ConfirmDialog from "../common/ConfirmDialog";
import {ComposeEmailControls} from "./ComposeEmailControls";
import CloseIcon from "@mui/icons-material/Close";
import AspectRatioIcon from "@mui/icons-material/AspectRatio";
import {popOutContent} from "../common/HideContentButton";
import {isTearOffWindow} from "../../App";
import SettingsIcon from '@mui/icons-material/Settings';
import {ComposeEmailSettingsDialog} from "./ComposeEmailSettingsDialog";
import {BccAddressAutoComplete, CcAddressAutoComplete, ToAddressAutoComplete} from "./EmailAddressAutoCompleteControls";
import {ValidationResult} from "../../validations/ValidationResult";

interface ComposeEmailHeaderProps {
    draftEmail: DbDraftEmail;
}

export const ComposeEmailHeader: FC<ComposeEmailHeaderProps> = ({draftEmail}) => {
    const dispatch = useAppDispatch();
    const store = useAppStore();

    const [validationResult, setValidationResult] = useState<ValidationResult>();

    const isMobile = useAppSelector(({system}) => system.isMobile);

    const {showSender} = useAppSelector(s => s.compose.composeOptions);

    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [validationDialogOpen, setValidationDialogOpen] = useState(false);

    const defaultAddress = useAppSelector(({system}) => system.defaultAddress);
    const isOffline = useAppSelector(({system}) => system.isOffline);

    const draftEmailId = draftEmail?.Uid;

    const senderAddress = extractEmailAddresses(draftEmail?.From)[0] || defaultAddress;

    const ccAddresses = draftEmail?.CcAddresses ?? [];
    const bccAddresses = draftEmail?.BccAddresses ?? [];

    const fileControlRef = useRef<FileUploadControlCallback | null>();

    function openAttachFileDialog() {
        fileControlRef.current?.openFileDialog();
    }

    function onChangeDraftEmail(changes: Partial<DbDraftEmail>) {
        dispatch(ComposeEmailActions.changeDraftEmail(changes));
    }

    const hasCC = ccAddresses.length > 0 || !!draftEmail.CcTextAddress;
    const hasBCC = bccAddresses.length > 0 || !!draftEmail.CcTextAddress;
    const hasSender = senderAddress !== defaultAddress;

    async function validateAndSendEmail(allowSendingWithoutSubject: boolean) {
        if (!draftEmail) {
            return;
        }

        const validationResult = validateEmailToSend(draftEmail, store.getState().compose.draftAttachments);

        if (validationResult.errors.length > 0) {
            setValidationResult(validationResult);
            setValidationDialogOpen(true);   // TODO: make the error text
            return;
        }

        if (!allowSendingWithoutSubject && !draftEmailHasSubject()) {
            setConfirmDialogOpen(true);
        } else {
            await ComposeEmailActions.sendEmail(draftEmail, isOffline, store);
            // TODO: fix this - we need the email to actually be sent!
            // if (isTearOffWindow) {
            //     window.close();
            // }
        }
    }

    function draftEmailHasSubject() {
        return draftEmail && draftEmail.Subject.trim().length > 0;
    }

    async function handleSendWithoutSubjectConfirmation(confirmed: boolean) {
        setConfirmDialogOpen(false);
        if (confirmed) {
            await validateAndSendEmail(true);
        }
    }

    function popOutComposer() {
        popOutContent();
        EmailLocation.stopComposingEmail();
    }

    function closeComposer() {
        if (isTearOffWindow) {
            window.close();
        } else {
            EmailLocation.stopComposingEmail();
        }
    }

    return (
        <>
            <div className="editor-column">
                <div className="button-container">
                    <Button variant="contained" color="primary"
                            onClick={() => validateAndSendEmail(false)}>Send</Button>

                    {isMobile && <Button variant="contained" color="secondary" onClick={openAttachFileDialog}>Attach</Button>}

                    {isTearOffWindow && <div style={{height: "32px"}}>&nbsp;</div>}
                    {!isTearOffWindow && !isMobile && (
                        <Button variant="contained"
                                color="inherit"
                                title="Pop out"
                                onClick={() => popOutComposer()}>
                            <AspectRatioIcon/>
                        </Button>)
                    }
                    {!isTearOffWindow && (
                        <Button variant="contained"
                                color="inherit"
                                title="Close"
                                onClick={() => closeComposer()}>
                            <CloseIcon/>
                        </Button>)
                    }

                    <ComposeEmailControls isSigned={!!draftEmail.IsSigned} isEncrypted={!!draftEmail.IsEncrypted}/>
                </div>

                <div className="editor-subject">
                    {(hasSender || showSender) && <SenderSelectControl senderAddress={senderAddress}/>}

                    <ToAddressAutoComplete addresses={draftEmail.ToAddresses} textAddress={draftEmail.ToTextAddress}/>

                    <CcAddressAutoComplete addresses={draftEmail.CcAddresses} textAddress={draftEmail.CcTextAddress}/>

                    <BccAddressAutoComplete addresses={draftEmail.BccAddresses} textAddress={draftEmail.BccTextAddress}/>

                    <TextField
                        label="Subject"
                        fullWidth
                        value={draftEmail?.Subject || ""}
                        onChange={e => onChangeDraftEmail({Subject: e.target.value})}
                    />

                    {!isMobile && <div className="editor-more-fields">
                        <Button variant="contained" color="secondary" onClick={openAttachFileDialog}>Attach</Button>
                        <span className="spacer">&nbsp;</span>
                        <Button onClick={() => dispatch(ComposeEmailActions.setComposeEmailOptionsDialogOpen(true))}
                                title="Compose Email Options">
                            <SettingsIcon/>
                        </Button>
                    </div>}
                </div>

            </div>
            {draftEmailId ? <AttachmentManagerView emailUid={draftEmailId} fileControlRef={fileControlRef}/> : null}

            <ErrorDialog
                open={validationDialogOpen}
                onClose={() => setValidationDialogOpen(false)}
                title={validationResult?.title ?? ""}
                message={validationResult?.errors.join("\n") ?? ""}
            />

            <ConfirmDialog
                open={confirmDialogOpen}
                onConfirm={handleSendWithoutSubjectConfirmation}
                title="Send Without Subject?"
                message="Are you sure you want to send this email without a subject?"
                confirmColour="warning"
            />

            <ComposeEmailSettingsDialog hasCC={hasCC} hasBCC={hasBCC} hasSender={hasSender}/>
        </>
    );
};
